import React, { useCallback, useContext, useState } from "react";
import { NotificationsContext } from "../../Notifications";
import activeCountries from "../../../activeCountries";
import { useIntl, FormattedMessage } from "react-intl";
import apiRequest from "../../../services/apiRequest";

import Input from "../../Input";
import Modal from "../../Modal";
import Toggle from "../../Toggle";
import Button from "../../Button";
import Placeholder from "../../Placeholder";
import RadioGroup from "../../RadioGroup";
import useRoles from "./hooks/useRoles";
import CheckboxGroup from "../../CheckboxGroup";
import {PRIVILEGED_ROLES} from "../../../constants";

function AddUserDialog({ onConfirm, onClose, loading, roles }) {

  // context
  const { push } = useContext(NotificationsContext);

  // hooks
  const intl = useIntl();

  // state
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [sfdcId, setSfdcId] = useState();
  const [lastName, setLastName] = useState("");
  const [roleId, setRoleId] = useState("");
  const [flgActive, setFlgActive] = useState(true);
  const [countries, setCountries] = useState([]);

  // utilities
  const internalRoles = roles
    ? roles.filter((x) => [...PRIVILEGED_ROLES, "field"].indexOf(x.name) >= 0)
    : [];

  const _onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (!roleId) {
        push({
          title: intl.formatMessage({ id: "need_to_select_a_role" }),
          type: "error",
        });
        return;
      }

      if (!email || !firstName || !lastName) {
        push({
          title: intl.formatMessage({ id: "enter_name_or_email" }),
          type: "error",
        });
        return;
      }

      if (countries.length === 0) {
        push({
          title: intl.formatMessage({ id: "select_countries" }),
          type: "error",
        });
        return;
      }

      await onConfirm({
        email,
        firstname: firstName,
        lastname: lastName,
        role_id: roleId,
        flg_active: flgActive,
        flg_internal: true,
        id_salesforce: sfdcId,
        countries: countries.join(";"),
      });
    },
    // eslint-disable-next-line
    [
      email,
      firstName,
      lastName,
      roleId,
      flgActive,
      countries,
      push,
    ]
  );

  return (
    <Modal onExit={onClose}>
      <div className="m-8">
        <h3 className="text-center text-2xl font-extrabold text-black-500">
          <FormattedMessage id="add_internal_user" />
        </h3>
      </div>

      <form id="add-user-form" onSubmit={_onSubmit}>
        <div>
          <Input
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
            }}
            label={intl.formatMessage({
              id: "name",
            })}
            type="text"
            errorMessage={intl.formatMessage({
              id: "enter_name",
            })}
            required
          />
          <Input
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
            }}
            label={intl.formatMessage({
              id: "surname",
            })}
            type="text"
            errorMessage={intl.formatMessage({
              id: "enter_surname",
            })}
            required
          />
        </div>
        <div>
          <Input
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
            }}
            label="Email"
            type="email"
            errorMessage={intl.formatMessage({
              id: "enter_email",
            })}
            required
          />
        </div>
        <div>
          <Input
            value={sfdcId}
            onChange={(e) => {
              setSfdcId(e.target.value);
            }}
            label="ID Salesforce"
            type="text"
          />
        </div>
        <div className="mb-4">
          {internalRoles ? (
            <div>
              <RadioGroup
                horizontal
                title={intl.formatMessage({
                  id: "role",
                })}
                options={internalRoles.map((role) => ({
                  value: role.id,
                  name:
                    role.name.charAt(0).toUpperCase() + role.name.slice(1),
                }))}
                currentValue={roleId}
                onChange={(value) => {
                  setRoleId(value);
                }}
              />
            </div>
          ) : (
            <div>
              <Placeholder width="w-4/5" />
              <Placeholder width="w-3/5" />
            </div>
          )}
        </div>
        <CheckboxGroup
          labelClasses="font-bold"
          horizontal
          title={intl.formatMessage({ id: "companies" })}
          options={activeCountries.map((i) => ({
            value: i.code,
            name: i.name,
          }))}
          values={countries}
          onChange={(value) => {
            if (countries.indexOf(value) >= 0) {
              setCountries([...countries.filter((v) => v !== value)]);
            } else {
              setCountries([...countries, value]);
            }
          }}
        />
        <div className="mb-6">
          <Toggle
            vertical
            label={intl.formatMessage({
              id: "active_user",
            })}
            checked={flgActive}
            onChange={() => setFlgActive(!flgActive)}
          />
        </div>
        <div className="mt-4 flex items-end justify-end">
          <Button submitting={loading} type="submit">
            <FormattedMessage id="add" />
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export default AddUserDialog;
